// deadlineCalendar.js — the rules behind the Deadline Calendar, pure so they can be tested
// (docs/calendar-investigation.md, decisions 2026-10-14).
//
// ⛔ THE BASE ROW IS A CACHE OF THE LATEST NOTICE. parcels.notice_date / appeal_deadline hold the notice for the
// parcel's latest notice year; each year's own notice lives on parcel_values (one row per parcel, year and
// jurisdiction). The scan used to write the base for whatever year it scanned, so an older notice replaced the
// current one on the calendar. Both the scan (may it write the base?) and the calendar (which year is current?)
// ask latestNoticeYear, so they cannot disagree.
import { APPEAL_STATUS } from '../constants/statusCodes';

const yearOf = (v) => {
  const m = String(v ?? '').match(/^(\d{4})/);
  return m ? Number(m[1]) : null;
};

// The latest year this parcel has a notice for: the newest parcel_values row carrying a notice date, or the base
// notice date's year, whichever is later. noticeRows: [{ tax_year, notice_date }]. → Number | null
export function latestNoticeYear(noticeRows, baseNoticeDate) {
  let best = yearOf(baseNoticeDate);
  (noticeRows || []).forEach((r) => {
    if (!r.notice_date) return;
    const y = yearOf(r.tax_year);
    if (y != null && (best == null || y > best)) best = y;
  });
  return best;
}

// The notice scan writes the base notice date / deadline only for the latest represented year. noticeRows already
// include the row just scanned. A year the parcel isn't represented in never moves the base.
export function noticeScanWritesBase({ year, noticeRows, baseNoticeDate, represented }) {
  if (!represented) return false;
  const y = yearOf(year);
  const latest = latestNoticeYear(noticeRows, baseNoticeDate);
  return y != null && (latest == null || y >= latest);
}

// The year's filing deadline from its notice rows: the earliest deadline among the year's jurisdictions, with that
// row's notice date. A year with notice dates but no deadline gives the earliest notice and no deadline (the caller
// computes one from the state rule). → { notice_date, appeal_deadline, jurisdiction } | null
export function yearNotice(rowsForYear) {
  const rows = (rowsForYear || []).filter((r) => r.notice_date || r.appeal_deadline);
  if (!rows.length) return null;
  const withDl = rows.filter((r) => r.appeal_deadline).sort((a, b) => String(a.appeal_deadline).localeCompare(String(b.appeal_deadline)));
  const pick = withDl[0] || rows.filter((r) => r.notice_date).sort((a, b) => String(a.notice_date).localeCompare(String(b.notice_date)))[0];
  return { notice_date: pick.notice_date || null, appeal_deadline: pick.appeal_deadline || null, jurisdiction: pick.jurisdiction || null };
}

// Has the year's filing deadline been dealt with? (Philip, 2026-10-14)
//   Appeal Open / Withdrawn / Denied, a level beyond Proposed, a filed date, or an accepted decision on an appeal level → Filed ✓
//   Value Accepted, or an accepted decision on Proposed                                                     → No appeal ✓
// status: the year's parcel_year_data.appeal_status · appeals: the year's parcel_appeals rows · decisions: the year's
// parcel_appeal_decisions rows. → 'Filed ✓' | 'No appeal ✓' | null
export function satisfiedLabel({ status, appeals = [], decisions = [] }) {
  if (status === APPEAL_STATUS.appeal_open || status === APPEAL_STATUS.withdrawn || status === APPEAL_STATUS.denied) return 'Filed ✓';
  if (appeals.some((a) => (a.appeal_level && a.appeal_level !== 'proposed') || a.filed_date)) return 'Filed ✓';
  if (decisions.some((d) => d.decision === 'accepted' && d.appeal_level !== 'proposed')) return 'Filed ✓';
  if (status === APPEAL_STATUS.value_accepted) return 'No appeal ✓';
  if (decisions.some((d) => d.decision === 'accepted' && d.appeal_level === 'proposed')) return 'No appeal ✓';
  return null;
}

// The consultant a calendar row shows and filters on: the year's assignment, then the property's consultant_firm,
// then the parcel's old text. An assignment that ENDED (a tombstone: a row with no firm) means no consultant — it
// does not fall back to the text it replaced. assignment: { company } | undefined (resolveFirmAssignmentsBulk).
export function calendarConsultant({ assignment, propertyFirm, parcelFirm }) {
  if (assignment) return assignment.company || null;
  const t = (v) => (v && String(v).trim()) || null;
  return t(propertyFirm) || t(parcelFirm);
}
